import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Award, BookOpen, Clock, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState, LoadingGrid, inr } from "@/components/bits";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { payWithPayU } from "@/lib/payu";

export default function Courses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/public/courses${category !== "all" ? `?category=${category}` : ""}`)
      .then(({ data }) => setCourses(data.courses))
      .finally(() => setLoading(false));
  }, [category]);

  const enroll = async (course) => {
    if (!user) {
      navigate("/login");
      return;
    }
    setBusyId(course.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "course",
        ref_id: course.id,
        origin_url: window.location.origin,
      });
      payWithPayU(data.payu_url, data.params);
    } catch (e) {
      toast.error(errMsg(e));
      setBusyId(null);
    }
  };

  const categories = ["all", ...new Set(courses.map((c) => c.category))];

  return (
    <div className="pt-16 min-h-screen" data-testid="courses-page">
      <div className="navy-bg noise-overlay py-16">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-white">Learning Marketplace</h1>
          <p className="text-white/60 mt-2 max-w-xl">
            Courses from verified training institutes, mapped to real job roles. Certificates are issued by the institute and verifiable on Job Spring.
          </p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex gap-2 flex-wrap mb-8">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`category-${c}`}
              className={`text-sm font-medium rounded-full px-4 py-2 border transition-colors capitalize ${
                category === c ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "bg-white border-border hover:border-[#2563EB]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        {loading ? (
          <LoadingGrid count={3} />
        ) : courses.length === 0 ? (
          <EmptyState title="No courses in this category yet" description="New courses are added after admin review." testId="courses-empty" />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((c) => (
              <div key={c.id} className="bg-white border border-border/60 rounded-2xl p-7 card-hover flex flex-col" data-testid={`course-card-${c.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-[#2563EB] bg-[#2563EB]/8 rounded-full px-3 py-1">{c.category}</span>
                  {c.certificate && (
                    <span className="flex items-center gap-1 text-xs text-[#F5A623] font-semibold">
                      <Award className="w-3.5 h-3.5" /> Certificate
                    </span>
                  )}
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#0B1B3D] mt-4">{c.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1 line-clamp-3">{c.description}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground mt-4">
                  <span className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> {c.institute_name}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {c.duration}</span>
                  <span className="capitalize">{c.mode}</span>
                </div>
                <div className="flex items-center justify-between mt-6 pt-5 border-t border-border/60">
                  <div>
                    {c.discount > 0 && <span className="text-xs text-muted-foreground line-through mr-2">{inr(c.price)}</span>}
                    <span className="font-heading font-bold text-lg text-[#0B1B3D]">{inr(c.price - (c.discount || 0))}</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => enroll(c)}
                    disabled={busyId === c.id}
                    className="rounded-full bg-[#0B1B3D] hover:bg-[#13264f]"
                    data-testid={`enroll-btn-${c.id}`}
                  >
                    <BookOpen className="w-4 h-4 mr-1.5" /> {busyId === c.id ? "Redirecting…" : "Enroll"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
